import { format } from 'date-fns';

import { getAuthHeaders } from '../api/auth-headers';
import { projectId } from '../../../utils/supabase/info';
import { fetchImageForDocx } from './docx-image';
import { applyBatchUpdate, createDoc, DOCS_SCOPE, renderLines, type LineSpec } from './export-docs';
import type { SeedingComment } from '../../seeding-report/lib/pick-comments';
import type { CommentCardImage } from '../../seeding-report/lib/render-comment-card';
import {
  cardSize,
  IMAGE_BOX,
  type TopVideoRow,
  type TopVideosParams,
  type TopVideosSection,
} from './export-top-videos-docx';
import { formatMetricsLine } from './format-metrics-ru';
import { getAccessToken } from './google-auth';
import { cleanPermalink } from './top-videos';

/**
 * «Топ видео» новым Google-документом, а не скачанным файлом.
 *
 * Просьба Артура 07.10.2026 — та же кнопка, что уже стоит в «Отработано»
 * (seeding-report/lib/export-seeding-gdoc): вход через Google Identity,
 * createDoc, renderLines и window.open на готовый документ.
 *
 * Оформление повторяет Word-версию (export-top-videos-docx): Arial 14, тема
 * жирным с висящим номером, ссылка и кадр глубже, строка метрик жирная по
 * ширине с красной строкой, каждый аккаунт — с новой страницы. Отступы там в
 * твипах, здесь в PT: 720 твипов = 36 PT.
 *
 * Кадры приходится заливать в хранилище: insertInlineImage берёт только
 * публичный адрес, а адреса CDN площадок Google скачать может и не дать.
 */

const UPLOAD_URL = `https://${projectId}.supabase.co/functions/v1/make-server-bde4f709/instagram/upload-image`;

const FONT = 'Arial';
const FONT_SIZE_PT = 14;
const INDENT_NUMBER_PT = 36;
const INDENT_BODY_PT = 54;
const FIRST_LINE_PT = 36;
const LINK_COLOR = { red: 0x46 / 255, green: 0x78 / 255, blue: 0x86 / 255 };
/** Точки картинки при 96 dpi — в PT для Docs. */
const PX_TO_PT = 0.75;

const HEADING_LINE = 'Анализ топовых видео и комментариев канала';

function sectionSubject(section: TopVideosSection): string {
  const kind = section.platform === 'tiktok' ? 'Tiktok' : 'Instagram';
  return `${kind}-канала «${section.accountTitle.replace(/^@/, '')}»`;
}

type ParagraphKind = 'sectionStart' | 'heading' | 'body' | 'metrics' | 'comments';

/** Комментарий текстом — если карточка не нарисовалась или не залилась. */
function commentFallback(c: SeedingComment): string {
  return `${c.nickname || c.username}${c.date ? ` · ${c.date}` : ''}\n${c.text}\n♡ ${c.likeCount.toLocaleString('ru-RU')}`;
}

/**
 * Текст документа, оформление и места под кадры — без сети, чтобы проверять
 * в тесте. `slots[i].line` — пустая строка, куда встанет кадр ролика.
 */
export function buildTopVideosDocRequests(params: TopVideosParams): {
  text: string;
  requests: object[];
  lineStarts: number[];
  slots: Array<{ line: number; row: TopVideoRow }>;
  /** Пустые строки под карточки комментариев, по одной на комментарий. */
  cardSlots: Array<{ line: number; comment: SeedingComment }>;
} {
  const sections = params.sections.filter((s) => s.rows.length > 0);
  if (sections.length === 0) throw new Error('Нет данных для экспорта');

  const lines: LineSpec[] = [];
  const kinds: Array<ParagraphKind | null> = [];
  const slots: Array<{ line: number; row: TopVideoRow }> = [];
  const cardSlots: Array<{ line: number; comment: SeedingComment }> = [];
  const push = (line: LineSpec, kind: ParagraphKind | null = null) => {
    lines.push(line);
    kinds.push(kind);
  };

  sections.forEach((section, s) => {
    push({ text: HEADING_LINE, center: true, bold: true }, s > 0 ? 'sectionStart' : null);
    push({ text: sectionSubject(section), center: true, bold: true });
    push({ text: '' });
    section.rows.forEach((row, i) => {
      push({ text: [`${i + 1}.`, row.theme].filter(Boolean).join(' '), bold: true }, 'heading');
      const link = cleanPermalink(row.permalink);
      push({ text: link, linkUrl: link }, 'body');
      if (row.imageUrl) {
        slots.push({ line: lines.length, row });
        push({ text: '' }, 'body');
      }
      push({ text: formatMetricsLine(row.metrics), bold: true }, 'metrics');
      if (row.comments && row.comments.length > 0) {
        push({ text: '' });
        push({ text: 'Топовые комментарии', bold: true }, 'comments');
        for (const comment of row.comments) {
          cardSlots.push({ line: lines.length, comment });
          push({ text: '' }, 'comments');
        }
      }
      push({ text: '' });
    });
  });

  const { text, formatRequests, lineStarts } = renderLines(lines, 1);

  const pt = (magnitude: number) => ({ magnitude, unit: 'PT' });
  const paragraph = (index: number, paragraphStyle: object, fields: string) => ({
    updateParagraphStyle: {
      range: { startIndex: lineStarts[index], endIndex: lineStarts[index] + lines[index].text.length + 1 },
      paragraphStyle,
      fields,
    },
  });

  const styleRequests: object[] = [];
  kinds.forEach((kind, i) => {
    if (kind === 'sectionStart') {
      styleRequests.push(paragraph(i, { pageBreakBefore: true }, 'pageBreakBefore'));
    } else if (kind === 'heading') {
      // Номер висит слева, перенос темы выравнивается по тексту, а не по цифре.
      styleRequests.push(
        paragraph(
          i,
          { indentStart: pt(INDENT_BODY_PT), indentFirstLine: pt(INDENT_NUMBER_PT), keepWithNext: true },
          'indentStart,indentFirstLine,keepWithNext',
        ),
      );
    } else if (kind === 'body') {
      styleRequests.push(
        paragraph(
          i,
          { indentStart: pt(INDENT_BODY_PT), indentFirstLine: pt(INDENT_BODY_PT), keepWithNext: true },
          'indentStart,indentFirstLine,keepWithNext',
        ),
      );
    } else if (kind === 'comments') {
      styleRequests.push(paragraph(i, { indentFirstLine: pt(FIRST_LINE_PT), keepWithNext: true }, 'indentFirstLine,keepWithNext'));
    } else if (kind === 'metrics') {
      styleRequests.push(
        paragraph(i, { alignment: 'JUSTIFIED', indentFirstLine: pt(FIRST_LINE_PT) }, 'alignment,indentFirstLine'),
      );
    }
    if (lines[i].linkUrl) {
      styleRequests.push({
        updateTextStyle: {
          range: { startIndex: lineStarts[i], endIndex: lineStarts[i] + lines[i].text.length },
          textStyle: { underline: true, foregroundColor: { color: { rgbColor: LINK_COLOR } } },
          fields: 'underline,foregroundColor',
        },
      });
    }
  });

  const end = 1 + text.length;
  const requests: object[] = [
    { insertText: { location: { index: 1 }, text } },
    {
      updateTextStyle: {
        range: { startIndex: 1, endIndex: end },
        textStyle: {
          weightedFontFamily: { fontFamily: FONT, weight: 400 },
          fontSize: pt(FONT_SIZE_PT),
        },
        fields: 'weightedFontFamily,fontSize',
      },
    },
    // Абзацы без отбивок и одинарный интервал — как в Word-версии.
    {
      updateParagraphStyle: {
        range: { startIndex: 1, endIndex: end },
        paragraphStyle: { spaceAbove: pt(0), spaceBelow: pt(0), lineSpacing: 100 },
        fields: 'spaceAbove,spaceBelow,lineSpacing',
      },
    },
    ...formatRequests,
    ...styleRequests,
  ];

  return { text, requests, lineStarts, slots, cardSlots };
}

/**
 * Кадр → JPEG в хранилище → публичный адрес и размер в PT. Пережимаем через
 * холст: Docs не берёт webp, а обложки TikTok бывают именно такими.
 */
async function uploadFrame(url: string): Promise<{ uri: string; width: number; height: number } | null> {
  try {
    const image = await fetchImageForDocx(url, IMAGE_BOX);
    if (!image) return null;
    const bitmap = await createImageBitmap(new Blob([image.data]));
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
    bitmap.close();

    const res = await fetch(UPLOAD_URL, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ imageBase64: canvas.toDataURL('image/jpeg', 0.9), contentType: 'image/jpeg' }),
    });
    if (!res.ok) {
      console.warn('[Топ видео] кадр не залился:', res.status);
      return null;
    }
    const { publicUrl } = (await res.json()) as { publicUrl?: string };
    if (!publicUrl) return null;
    return { uri: publicUrl, width: image.width * PX_TO_PT, height: image.height * PX_TO_PT };
  } catch (err) {
    console.warn('[Топ видео] кадр не залился:', err);
    return null;
  }
}

/**
 * Собирает документ и открывает его в новой вкладке. Возвращает адрес — он же
 * показывается человеку, если вкладку заблокировал браузер.
 *
 * Токен берётся до сборки отчёта (см. TopVideosReportCard): окно входа Google
 * открывается только сразу после клика, а сбор роликов идёт десятки секунд.
 */
async function uploadCard(card: CommentCardImage): Promise<string | null> {
  try {
    const res = await fetch(UPLOAD_URL, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ imageBase64: card.dataUrl, contentType: 'image/png' }),
    });
    if (!res.ok) {
      console.warn('[Топ видео] карточка не залилась:', res.status);
      return null;
    }
    const { publicUrl } = (await res.json()) as { publicUrl?: string };
    return publicUrl ?? null;
  } catch (err) {
    console.warn('[Топ видео] карточка не залилась:', err);
    return null;
  }
}

export async function generateTopVideosGoogleDoc(
  params: TopVideosParams,
  token: string,
  onProgress: (message: string) => void = () => {},
): Promise<string> {
  const { requests, lineStarts, slots, cardSlots } = buildTopVideosDocRequests(params);

  onProgress('Создание документа...');
  const docId = await createDoc(token, `${format(new Date(), 'yyyy.MM.dd')} топ видео`);
  await applyBatchUpdate(token, docId, requests);

  type Insert = { line: number; image: { uri: string; width: number; height: number } | null; fallback?: string };
  const inserts: Insert[] = [];
  if (slots.length > 0) {
    onProgress('Кадры роликов...');
    const frames = await Promise.all(slots.map(({ row }) => uploadFrame(row.imageUrl)));
    slots.forEach((slot, i) => inserts.push({ line: slot.line, image: frames[i] }));
  }
  if (cardSlots.length > 0) {
    onProgress('Комментарии...');
    const uploaded = await Promise.all(
      cardSlots.map(async ({ comment }) => {
        const card = params.cards?.get(comment.id);
        if (!card) return null;
        const uri = await uploadCard(card);
        if (!uri) return null;
        const size = cardSize(card);
        return { uri, width: size.width * PX_TO_PT, height: size.height * PX_TO_PT };
      }),
    );
    cardSlots.forEach((slot, i) =>
      inserts.push({ line: slot.line, image: uploaded[i], fallback: commentFallback(slot.comment) }),
    );
  }

  // С конца: вставка сдвигает всё, что после неё, а то, что до, — нет.
  inserts.sort((a, b) => b.line - a.line);
  for (const insert of inserts) {
    const index = lineStarts[insert.line];
    if (insert.image) {
      try {
        await applyBatchUpdate(token, docId, [
          {
            insertInlineImage: {
              location: { index },
              uri: insert.image.uri,
              objectSize: {
                width: { magnitude: insert.image.width, unit: 'PT' },
                height: { magnitude: insert.image.height, unit: 'PT' },
              },
            },
          },
        ]);
        continue;
      } catch (err) {
        // Картинка не встала — отчёт всё равно нужен целиком.
        console.warn('[Топ видео] картинка не вставилась:', err);
      }
    }
    // Комментарий без карточки уходит текстом, кадр без картинки — пропуском.
    if (insert.fallback) {
      await applyBatchUpdate(token, docId, [{ insertText: { location: { index }, text: insert.fallback } }]);
    }
  }

  const url = `https://docs.google.com/document/d/${docId}/edit`;
  window.open(url, '_blank');
  return url;
}

/** Вход в Google — первым делом по клику, пока браузер ещё разрешает окно. */
export async function getTopVideosDocsToken(): Promise<string> {
  if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
    throw new Error('VITE_GOOGLE_CLIENT_ID не задан в .env');
  }
  return getAccessToken(DOCS_SCOPE);
}
